<template>
  <div v-if="loading" class="flex h-full justify-center items-center">
    <loading-spinner diameter="64px"/>
  </div>
  <alert-badge
      v-else-if="error.title"
      isDanger
      :title="error.title"
      :body="error.body"
      @dismissed="
      () => {
        error.title = ''
        error.body = ''
      }
    "
  />
  <el-form
      v-else
      @submit.prevent="validateSubmit('commissionForm')"
      novalidate
      :model="commission"
      :rules="rules"
      label-position="top"
      ref="commissionForm"
  >
    <input-section>
      <div class="flex flex-col md:flex-row justify-end">
        <div class="md:w-4/12 m-2">
          <horizontal-button
              :title="$t('actions.saveChanges')"
              isSuccess
              faIcon="save"
              @click.prevent="validateSubmit('commissionForm')"
          />
        </div>

        <div class="md:w-4/12 m-2" v-if="enableClearForm">
          <horizontal-button
              :title="$t('actions.clearButton')"
              isDanger
              faIcon="trash"
              @click.prevent="clearForm('commissionForm')"
          />
        </div>
      </div>
    </input-section>

    <!-- @submit errors -->
    <alert-badge
        isDanger
        :title="error.title"
        :body="error.body"
        @dismissed="
        () => {
          error.title = ''
          error.body = ''
        }
      "
    />

    <input-section>
      <h1>{{ t('commissionInfo') }}</h1>

      <el-form-item :label="t('name')" prop="name" class="input-label">
        <el-input
            id="enName"
            v-model="commission.name"
            placeholder=""
            maxlength="255"
            show-word-limit
            clearable
            required
        >
        </el-input>
      </el-form-item>

      <el-form-item :label="t('stores')" prop="stores" class="input-label">
        <el-select
            class="w-full"
            filterable
            clearable
            v-model="commission.stores"
            multiple
            placeholder=""
            :class="{'select-all': !commission.includeAllStores}"
        >
          <template #header>
            <el-checkbox
                v-model="commission.includeAllStores"
                @change="() => {
                     commission.stores = [];
                }"
            >
              {{ t('all') }}
            </el-checkbox>
          </template>
          <template #prefix>
             <span v-if="commission.includeAllStores">
               {{ t('all') }}
             </span>
          </template>
          <el-option
              v-for="item in locations"
              :disabled="commission.includeAllStores"
              :key="item.id"
              :label="item.enName"
              :value="item.id"
          />
        </el-select>
      </el-form-item>
      <div class="flex gap-2">
        <h1>{{ t('rules') }}</h1>
        <button
            class="px-3 rounded-lg bg-blue-200 hover:bg-blue-300 active:bg-blue-400 text-xl"
            type="button" @click="addRule">+
        </button>
      </div>
      <draggable
          tag="ul"
          :list="commission.rules"
          class="list-group"
          handle=".handle"
          item-key="name"
          @change="reorder"
      >
        <template #item="{ element, index }">
          <li class="p-2 my-2 flex items-center gap-2">
            <div class="handle cursor-pointer">
              <svg width="22" height="12" viewBox="0 0 22 12" fill="none"
                   xmlns="http://www.w3.org/2000/svg">
                <line x1="1" y1="1" x2="21" y2="1" stroke="#818181"
                      stroke-width="2" stroke-linecap="round"/>
                <line x1="1" y1="6" x2="21" y2="6" stroke="#818181"
                      stroke-width="2" stroke-linecap="round"/>
                <line x1="1" y1="11" x2="21" y2="11" stroke="#818181"
                      stroke-width="2" stroke-linecap="round"/>
              </svg>
            </div>
            <div class="w-2/6">
              <el-select
                  class="w-full"
                  filterable
                  clearable
                  v-model="element.ruleType"
                  placeholder=""
                  @change="() => {element.values = []}"
              >
                <el-option
                    v-for="item in ruleTypes"
                    :key="item"
                    :label="t(item)"
                    :value="item"
                />
              </el-select>
            </div>
            <div class="w-full">
              <el-select
                  v-if="!element.ruleType"
                  placeholder=""
                  disabled
              >
              </el-select>
              <el-tree-select
                v-if="element.ruleType == 'category'"
                v-model="element.values"
                :data="categoriesTree"
                multiple
                :render-after-expand="false"
                show-checkbox
                check-strictly
                check-on-click-node
                style="width: 100%"
                placeholder=""
                :class="{'select-all': !element.includeAll}"
              >
              <template #header>
                  <el-checkbox
                      v-model="element.includeAll"
                      @change="() => {
                        element.values = [];
                      }"
                  >
                    {{ t('all') }}
                  </el-checkbox>
                </template>
                <template #prefix>
                  <span v-if="element.includeAll">
                    {{ t('all') }}
                  </span>
                </template>
              </el-tree-select>
              <!-- <el-select
                  v-if="element.ruleType == 'category'"
                  filterable
                  clearable
                  class="w-full"
                  multiple
                  v-model="element.values"
                  placeholder=""
                  :class="{'select-all': !element.includeAll}"
              >
                <template #header>
                  <el-checkbox
                      v-model="element.includeAll"
                      @change="() => {
                        element.values = [];
                      }"
                  >
                    {{ t('all') }}
                  </el-checkbox>
                </template>
                <template #prefix>
                  <span v-if="element.includeAll">
                    {{ t('all') }}
                  </span>
                </template>

                <el-option
                    v-for="item in categories"
                    :disabled="element.includeAll"
                    :key="item.id"
                    :label="item.name"
                    :value="item.id"
                />
              </el-select> -->
              <el-select
                  v-if="element.ruleType == 'tag'"
                  filterable
                  clearable
                  class="w-full"
                  multiple
                  v-model="element.values"
                  placeholder=""
                  :class="{'select-all': !element.includeAll}"
              >
                <template #header>
                  <el-checkbox
                      v-model="element.includeAll"
                      @change="() => {
                        element.values = [];
                      }"
                  >
                    {{ t('all') }}
                  </el-checkbox>
                </template>
                <template #prefix>
                    <span v-if="element.includeAll">
                      {{ t('all') }}
                    </span>
                </template>
                <el-option
                    v-for="item in tags"
                    :disabled="element.includeAll"
                    :key="item.id"
                    :label="item.name"
                    :value="item.id"
                />
              </el-select>
              <el-select
                  v-if="element.ruleType == 'brand'"
                  filterable
                  clearable
                  class="w-full"
                  multiple
                  v-model="element.values"
                  placeholder=""
              >
                <el-option
                    v-for="item in brands"
                    :key="item.id"
                    :label="item.name"
                    :value="item.id"
                />
              </el-select>
              <el-select
                  v-if="element.ruleType == 'item'"
                  filterable
                  clearable
                  class="w-full"
                  multiple
                  v-model="element.values"
                  placeholder=""
                  :class="{'select-all': !element.includeAll}"
              >
                <template #header>
                  <el-checkbox
                      v-model="element.includeAll"
                      @change="() => {
                        element.values = [];
                      }"
                  >
                    {{ t('all') }}
                  </el-checkbox>
                </template>
                <template #prefix>
                    <span v-if="element.includeAll">
                      {{ t('all') }}
                    </span>
                </template>
                <el-option
                    v-for="item in items"
                    :disabled="element.includeAll"
                    :key="item.id"
                    :label="item.name"
                    :value="item.id"
                />
              </el-select>
            </div>
            <div v-if="element.ruleType" class="w-2/6">
              <el-select
                  class="w-full"
                  filterable
                  clearable
                  v-model="element.commissionType"
                  placeholder=""
              >
                <el-option
                    v-for="item in ['amount', 'percentage']"
                    :key="item"
                    :label="t(item)"
                    :value="item"
                />
              </el-select>
            </div>
            <div v-if="!element.ruleType" class="w-2/6">
              <el-select
                  class="w-full"
                  placeholder=""
                  disabled
              >
              </el-select>
            </div>
            <div v-if="element.commissionType == 'amount' && element.ruleType"
                 class="w-2/6">
              <money-input

                  id="list-price"
                  :currency="$ctx.currency"
                  v-model="element.commissionValue"
                  @dinero-created="(dinero) => (element.commissionDinero = dinero)"
                  ref="listPriceAmountRef"
              />

            </div>
            <div
                v-if="element.commissionType == 'percentage' && element.ruleType"
                class="w-2/6 flex items-center gap-2">
              <span>%</span>
              <el-input-number
                  class="custom-input-number"
                  v-model="element.commissionValue"
                  :step="1"
                  :min="0"
                  :precision="1"
                  :controls="false"
              />
            </div>
            <div v-if="!element.commissionType" class="w-2/6">
              <el-input disabled></el-input>
            </div>

            <button type="button" @click="deleteRule(element, index)">
              <i class="fa fa-times close"></i>
            </button>
          </li>
        </template>
      </draggable>

      <el-form-item :label="t('employees')" prop="employees"
                    class="input-label">
        <el-select
            class="w-full"
            filterable
            clearable
            v-model="commission.employees"
            multiple
            placeholder=""
            :class="{'select-all': !commission.includeAllEmployees}"
        >
          <template #header>
            <el-checkbox
                v-model="commission.includeAllEmployees"
                @change="() => {
                    commission.employees = [];
                }"
            >
              {{ t('all') }}
            </el-checkbox>
          </template>
          <template #prefix>
              <span v-if="commission.includeAllEmployees">
                {{ t('all') }}
              </span>
          </template>
          <el-option
              v-for="item in employees"
              :disabled="commission.includeAllEmployees"
              :key="item.id"
              :label="item.fullName"
              :value="item.id"
          />
        </el-select>
      </el-form-item>
      <el-form-item :label="t('period')" prop="period" class="input-label">
        <el-select
            class="w-full"
            filterable
            clearable
            v-model="commission.period"
            placeholder=""
        >
          <el-option
              v-for="item in periods"
              :key="item"
              :label="t(item)"
              :value="item"
          />
        </el-select>
      </el-form-item>

      <div v-if="commission.period == 'specificDate'" class="flex gap-2">
        <el-form-item :label="t('from')" prop="fromDate"
                      class="input-label w-full">
          <el-date-picker
              style="width: 100%;"
              v-model="commission.fromDate"
              type="date"
              :placeholder="t('from')"
              size="large"
              :popper-class="$ctx.getDir() === 'rtl' ? 'rtl' : ''"
          >
          </el-date-picker>
        </el-form-item>
        <el-form-item :label="t('to')" prop="toDate" class="input-label w-full">
          <el-date-picker
              style="width: 100%;"
              v-model="commission.toDate"
              type="date"
              :placeholder="t('to')"
              size="large"
              :popper-class="$ctx.getDir() === 'rtl' ? 'rtl' : ''"
          >
          </el-date-picker>
        </el-form-item>
      </div>
    </input-section>
    <input-section>
      <el-form-item prop="disabled" class="input-label" style="margin: 0 auto">
        <el-checkbox v-model="commission.disabled" id="disabled">
          {{ t('disabled') }}
        </el-checkbox>
      </el-form-item>
    </input-section>

  </el-form>
</template>

<script lang="ts">
import {defineComponent, onMounted, ref} from 'vue'

// components
import AlertBadge from '@/components/AlertBadge.vue'
import HorizontalButton from '@/components/HorizontalButton.vue'
import InputSection from '@/components/form/InputSection.vue'

// models
import Commission, {
  commissionI18nMessages,
  CommissionRule
} from '@/models/hr/Commission'
import {useI18n} from 'vue-i18n'
import {ElDatePicker, ElForm} from 'element-plus'
import User from "@/models/user/User";
import Location from "@/models/company/Location";
import Item from "@/models/inventory/Item";
import Tag from "@/models/inventory/Tag";
import Category from "@/models/inventory/Category";
import {useHTTP} from "@/plugins/http";
import draggable from "vuedraggable";
import MoneyInput from "@/components/form/MoneyInput.vue";
import Brand from "@/models/inventory/Brand";
import LoadingSpinner from "@/components/LoadingSpinner.vue";

export default defineComponent({
  name: 'commission-form',
  components: {
    LoadingSpinner,
    MoneyInput,
    ElDatePicker,
    AlertBadge,
    HorizontalButton,
    InputSection,
    draggable
  },
  props: {
    initCommission: {
      type: Commission,
      required: true
    },
    submitAction: {
      type: Function,
      required: true
    },
    successMsg: {
      type: String,
      required: true
    },
    enableClearForm: {
      type: Boolean,
      required: false
    },
    clearFormOnSubmit: {
      type: Boolean,
      default: false
    }
  },

  setup() {
    const {t} = useI18n({
      messages: {
        en: {
          ...commissionI18nMessages.en,
        },
        ar: {
          ...commissionI18nMessages.ar,
        }
      },
      useScope: 'global'
    })

    const http = useHTTP();

    const locations = ref<Array<Location>>();
    const items = ref<Array<Item>>();
    const tags = ref<Array<Tag>>();
    const brands = ref<Array<Brand>>();
    const categories = ref<Array<Category>>();
    const categoriesTree = ref<Array<any>>();
    const employees = ref<Array<User>>();
    const periods = ref([
      'open',
      // 'specificDate'
    ]);
    const ruleTypes = ref([
      'category',
      'tag',
      'brand',
      'item',
    ])

    const loading = ref(true)
    const error = ref({title: '', body: ''})

    onMounted(async () => {
      try {
        const locationsResp = await http.get<Location[]>(Location.ENDPOINT)
        locations.value = locationsResp.map((data) => Location.from(data))


        const itemsRes = await http.get<Item[]>(Item.ENDPOINT)
        items.value = itemsRes.map((data) => Item.from(data))

        const employeesRes = await http.get<User[]>(User.ENDPOINT)
        employees.value = employeesRes.map((data) => User.from(data))

        const tagsRes = await http.get<Tag[]>(Tag.ENDPOINT)
        tags.value = tagsRes.map((data) => Tag.from(data))

        const brandsRes = await http.get<Brand[]>(Brand.ENDPOINT)
        brands.value = brandsRes.map((data) => Brand.from(data))

        const categoriesRes = await http.get<Category[]>(Category.ENDPOINT)
        categories.value = categoriesRes.map((data) => Category.from(data))

        buildCategoriesTree()


        loading.value = false;
      } catch (e) {
        console.log(e);
        error.value.title = e.title
        error.value.body = e.body
      }

    })

    const buildCategoriesTree = () => {
      interface TreeNode {
        value: string
        label: string
        children?: TreeNode[]
      }
      
      const categoryMap = new Map<string, TreeNode>()
      
      // First, create nodes for all categories
      categories.value.forEach(category => {
        categoryMap.set(category.id, {
          value: category.id,
          label: category.name,
          children: []
        })
      })
      
      // Build the tree structure
      const rootCategories: TreeNode[] = []
      
      categories.value.forEach(category => {
        const node = categoryMap.get(category.id)
        
        if (category.parentID && categoryMap.has(category.parentID)) {
          // This category has a parent, add it to parent's children
          const parent = categoryMap.get(category.parentID)
          if (parent && node) {
            parent.children!.push(node)
          }
        } else {
          // This is a root category (no parent or parent doesn't exist)
          if (node) {
            rootCategories.push(node)
          }
        }
      })
      
      const cleanEmptyChildren = (node: TreeNode): void => {
        if (node.children && node.children.length === 0) {
          delete node.children
        } else if (node.children) {
          node.children.forEach(cleanEmptyChildren)
        }
      }
      
      rootCategories.forEach(cleanEmptyChildren)
      
      categoriesTree.value = rootCategories
    }

    return {
      t,
      locations,
      items,
      tags,
      brands,
      categories,
      categoriesTree,
      employees,
      periods,
      ruleTypes,
      loading,
      error
    }
  },

  data() {
    return {
      commission: this.initCommission,
      error: {title: '', body: ''},
      rules: {
        name: [
          {
            required: true,
            message: this.$t('validation.required'),
            trigger: 'blur'
          },
          {
            max: 255,
            message: this.$t('validation.maxLength', {len: 255}),
            trigger: 'blur'
          }
        ],
      }
    }
  },

  methods: {
    clearForm(formName: string) {
      const form = this.$refs[formName] as typeof ElForm
      form.resetFields()
    },

    async validateSubmit(formName: string) {
      const form = this.$refs[formName] as typeof ElForm
      form?.validate(async (valid: boolean) => {
        if (!valid) {
          document.getElementById('main')?.scrollTo({top: 0})
          return false
        }

        const resp = await this.submitAction(this.commission)

        if (resp) {
          this.error.title = resp?.title
          this.error.body = resp?.body
          document.getElementById('main')?.scrollTo({top: 0})
          return false
        } else {
          if (this.clearFormOnSubmit) {
            this.clearForm(formName)
          }

          this.$alertModal.showSuccess({title: this.successMsg, body: ''})
        }
        return true
      })
    },

    addRule() {
      if (!this.commission.rules) {
        this.commission.rules = [];
      }
      this.commission.rules?.push(new CommissionRule())
    },
    reorder() {
      this.commission.rules?.forEach((rule, index) => (rule.order = index))
    },
    deleteRule(rule: CommissionRule, index: number) {
      this.commission.rules?.splice(index, 1);
      this.reorder();
    }
  }
})
</script>

<style scoped>
form > h1 {
  @apply text-lg py-3 font-bold;
}

form > h2 {
  @apply py-3 font-bold;
}

formt > h3 {
  @apply py-3 font-bold;
}

form {
  @apply flex flex-col items-center;
}
</style>
